import { useCallback, useEffect, useState } from "react";

import { generatePath, useParams, useRouteMatch } from "react-router-dom";

import { getReportMentions } from "@/api/endpoints/mentions";
import ListMention from "@/containers/ListMention";
import useSearchParams from "@/hooks/useSearchParams";
import { ROUTES } from "@/routes_new";
import { ensureIsDate, formatDateForApi } from "@/utils";
import Modal, {
  ModalContent,
  ModalHeader,
  ModalTitle,
} from "@components/Modal";
import Pagination from "@components/Pagination";
import Sentiment from "@components/Sentiment";
import Spinner from "@components/Spinner";

import styles from "./MentionsModal.module.css";

const MentionsList = (props) => {
  const { uuid } = useParams();
  const isInsight = useRouteMatch(ROUTES.MENTION_INSIGHTS);

  return props.mentions.map((mention, i) => {
    let url = mention.public_url;
    if (uuid && isInsight) {
      url = generatePath(ROUTES.MENTIONS_ANCHOR, {
        search_uuid: uuid,
        mention_uuid: mention.public_id,
      });
    }
    return (
      <ListMention
        key={i}
        episode_public_url={url}
        channel_name={mention.channel_title}
        title={mention.episode_title}
        source={mention.channel_type}
        external_source={mention.external_source}
        published_date={mention.episode_published_date}
        text={mention.text}
      >
        {mention.sentiment && (
          <Sentiment value={mention.sentiment} className={styles.icon} />
        )}
      </ListMention>
    );
  });
};

const MentionsModal = (props) => {
  const [mentions, setMentions] = useState([]);
  const [meta, setMeta] = useState();
  const [urlFilters] = useSearchParams();
  const [previousFetchMentionsParams, setPreviousFetchMentionsParams] =
    useState();

  const fetchMentions = useCallback(
    (reportUuid, sources, filters, usePreviousParams = false, page = 1) => {
      let params;

      // pagination calls will use the previous params but change page
      if (!usePreviousParams) {
        const searches = sources
          .filter((s) => s.type === "search" || !s.type)
          .map((s) => s.id);
        const tags = sources.filter((s) => s.type === "tag").map((s) => s.id);

        // The API filters are built directly from chart labels right now, and the sentiment labels
        // are translated, which will break since the API wants english. Not worth rewriting this
        // logic just to fix this now, so simply map the values.
        const sentimentMapper = {
          positiva: "positive",
          negativa: "negative",
          neutrala: "neutral",
          positive: "positive",
          negative: "negative",
          neutral: "neutral",
        };
        const sentiment = filters.sentiment
          ? sentimentMapper[filters.sentiment.toLowerCase()]
          : "";

        const channelTypeMapper = {
          x: "twitter",
          podcast: "podcast",
          radio: "radio",
          youtube: "youtube",
          tiktok: "tiktok",
          tv: "tv",
          "web tv": "webtv",
          "manual uploads": "manual",
          instagram: "instagram",
          facebook: "facebook",
          linkedin: "linkedin",
          news: "news",
          nyhetsartiklar: "news",
        };

        const channelType = filters.mediaType
          ? channelTypeMapper[filters.mediaType.toLowerCase()]
          : "";

        params = {
          ...urlFilters,
          report: reportUuid,
          metric: filters.metric,
          searches: Array.isArray(searches) ? searches.join(",") : searches,
          tags: Array.isArray(tags) ? tags.join(",") : tags,
          start_date: formatDateForApi(ensureIsDate(filters.dates[0])),
          end_date: formatDateForApi(ensureIsDate(filters.dates[1])),
          category: filters.topic,
          sentiment: sentiment,
          channel_name: filters.channel_name,
          channel_type: channelType,
          pest_category: filters.pest_category,
          mention_kind: filters.mention_kind,
          mention_media_category: filters.mention_media_category,
          page: page,
        };
      } else {
        params = { ...previousFetchMentionsParams, page: page };
      }

      if (filters?.metric === "engagement") {
        params["exclude_channel_types"] = "news"; // news don't contribute to engagement
      }

      // store params so pagination can re-use them later
      setPreviousFetchMentionsParams({ ...params });

      // clear mentions so loader in modal appears
      setMentions(null);
      setMeta(null);

      getReportMentions(params)
        .then(({ data }) => {
          setMentions(data.mentions);
          setMeta(data.meta);
        })
        .catch((error) => {
          throw error;
        });
    },
    [
      setMentions,
      setMeta,
      setPreviousFetchMentionsParams,
      previousFetchMentionsParams,
      urlFilters,
    ]
  );

  useEffect(() => {
    const { report, sources, filters } = props.params;
    if (sources) fetchMentions(report, sources, { ...urlFilters, ...filters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.params, urlFilters]);

  // fetch mentions using the same params but increase page number
  const fetchMentionsPage = (page) => {
    fetchMentions(null, null, null, true, page);
  };

  return (
    <Modal show={props.show} onHide={props.onHide}>
      <ModalHeader closeButton>
        <ModalTitle>{L.MENTIONS_VIEW_HEADER}</ModalTitle>
      </ModalHeader>

      <ModalContent>
        {mentions && mentions.length ? (
          <MentionsList
            mentions={mentions}
            metric={props.params.filters?.metric}
          />
        ) : mentions ? (
          L.MENTIONS_VIEW_NO_HITS
        ) : (
          <div className="flex w-full justify-center">
            <Spinner className="flex self-center mb-5" />
          </div>
        )}
        <div className="mt-4">
          {meta && meta.total_num_pages > 1 && (
            <Pagination
              current={meta.page_number}
              max={meta.total_num_pages}
              next={() => fetchMentionsPage(meta.page_number + 1)}
              previous={() => fetchMentionsPage(meta.page_number - 1)}
            />
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export { MentionsModal };
